import {Database, useDataQuery, useItemQuery} from '@tnc/shared';
import {useEffect, useMemo, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useNavigate, useParams} from 'react-router';

import {FormFieldT} from '@/lib/types/form.types';
import {itemT} from '@/lib/types/item.types';
import {ratesT} from '@/lib/types/rateSheets.types';

import {usdFormatter} from '@/lib/helpers';
import {SupabaseInstance} from '@/lib/utils';

import {useNotifications} from '@/providers/components/notificationsProvider';

import FormInput from '@/components/form/FormInput';

type GroupedItem = {
  productId: number;
  rateId: number;
  productName: string;
  totalQuantity: number;
  wholesalePrice: number;
  factoredPrice: number;
  totalCost: number;
  wholesaleQuantity: number;
  factoredQuantity: number;
  items: (itemT & {Rates: ratesT})[];
  rate?: ratesT;
};

type InvoiceT = Database['public']['Tables']['Invoices']['Row'];

/**
 * Form Fields for the Invoice
 */
const formFields: FormFieldT<InvoiceT>[] = [
  {
    label: 'Contact Name',
    placeholder: 'Contact Name',
    name: 'invoice_contact_name',
    options: {
      required: 'Contact name is required',
      minLength: {value: 2, message: 'Name must be at least 2 characters'},
      maxLength: {value: 50, message: 'Name must be at most 50 characters'},
    },
  },
  {
    label: 'Address',
    name: 'invoice_contact_address',
    type: 'textarea',
    options: {
      required: 'Address is required',
      minLength: {value: 5, message: 'Address must be at least 5 characters'},
    },
  },
  {
    label: 'Contact Email',
    placeholder: 'Contact Email',
    name: 'invoice_contact_email',
    type: 'email',
    options: {
      required: 'Email is required',
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: 'Invalid email address',
      },
    },
  },
  {
    label: 'Phone Number',
    placeholder: 'Phone Number ex: +1-805-555-1212',
    type: 'tel',
    name: 'invoice_contact_phone',
    options: {
      required: 'Phone number is required',
      pattern: {
        value: /^\+?\d{1,4}?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/,
        message: 'Invalid phone number',
      },
    },
  },

  {
    label: 'Issue Date',
    name: 'invoice_date_issued',
    type: 'date',
    options: {
      required: 'Issue date is required',
      validate: (value: string) => {
        // Create dates and force them to be interpreted in local timezone
        const today = new Date();
        const [year, month, day] = value.split('-').map(Number);
        const issueDate = new Date(year, month - 1, day); // month is 0-based in JS Date

        // Set both to midnight
        today.setHours(0, 0, 0, 0);
        issueDate.setHours(0, 0, 0, 0);

        return issueDate >= today || 'Issue date must be today or later';
      },
    },
  },
  {
    label: 'Due Date',
    name: 'invoice_due_date',
    type: 'date',
    options: {
      required: 'Due date is required',
      validate: (value: string) => {
        // Create dates and force them to be interpreted in local timezone
        const today = new Date();
        const [year, month, day] = value.split('-').map(Number);
        const issueDate = new Date(year, month - 1, day); // month is 0-based in JS Date

        // Set both to midnight
        today.setHours(0, 0, 0, 0);
        issueDate.setHours(0, 0, 0, 0);

        return issueDate >= today || 'Issue date must be today or later';
      },
    },
  },
  {
    label: 'Description',
    name: 'invoice_description',
    type: 'textarea',
    options: {
      minLength: {value: 10, message: 'Description must be at least 10 characters'},
      maxLength: {value: 500, message: 'Description must be at most 500 characters'},
    },
  },
];

/**
 * Create Invoice Page
 * @description This page is used to create an invoice for a retailer or distributor
 */
function CreateInvoicePage() {
  // Hooks
  const {id, type} = useParams();
  const navigate = useNavigate();
  const {toast} = useNotifications();

  // State
  const [selectedItems, setSelectedItems] = useState<GroupedItem[]>([]);
  const [selectedRate, setSelectedRate] = useState<ratesT | null>(null);
  const [showRateModal, setShowRateModal] = useState(false);

  // Data Queries
  const {data: distributor} = useItemQuery({
    table: 'Distributor',
    id: Number(id),
    columns: ['distributor_id', 'distributor_name', 'distributor_email', 'distributor_phone', 'distributor_address'],
    enabled: type === 'distributor' && !!id,
  });

  const {data: retailer} = useItemQuery({
    table: 'Retailer',
    id: Number(id),
    columns: ['retailer_id', 'retailer_name', 'retailer_email', 'retailer_phone', 'retailer_address'],
    enabled: type === 'retailer' && !!id,
  });

  // Form
  const formConfig = useForm<InvoiceT>({
    defaultValues: {
      invoice_date_issued: new Date().toISOString().split('T')[0],
      invoice_due_date: '',
      invoice_contact_name: retailer?.retailer_name,
      invoice_contact_email: retailer?.retailer_email,
      invoice_contact_phone: retailer?.retailer_phone,
      invoice_contact_address: retailer?.retailer_address,
      invoice_description: '',
    },
  });

  const {handleSubmit, trigger, reset, setValue} = formConfig;

  useEffect(() => {
    if (type === 'retailer' && retailer) {
      setValue('invoice_contact_name', retailer.retailer_name);
      setValue('invoice_contact_email', retailer.retailer_email);
      setValue('invoice_contact_phone', retailer.retailer_phone);
      setValue('invoice_contact_address', retailer.retailer_address);
    }
  }, [retailer, type, setValue]);

  const {data: inventoryItems, refetch: refetchInventoryItems} = useDataQuery({
    table: 'Item',
    all: true,
    columns: [
      '*',
      ['Rates', ['*']],
      ['Product', ['*']],
      ['Brand', ['*']],
      ['Invoice_Items', ['*', ['Invoices', ['*']]]],
    ],
    filters: [
      {
        column: 'item_inventory_type',
        operator: 'eq',
        value: 'retailer',
      },
      {
        column: 'fk_inventory_location_id',
        operator: 'eq',
        value: retailer?.retailer_id,
      },
      {
        column: 'fk_transaction_id',
        operator: 'is',
        value: null,
      },
      {
        column: 'fk_invoice_item_id',
        operator: 'is',
        value: null,
      },
    ],
    enabled: type === 'retailer' && !!id,
  });

  // Group items by product and rate
  const groupedItems = useMemo(() => {
    if (!inventoryItems || !inventoryItems?.data) return [];

    const groups: {[key: string]: GroupedItem} = {};

    inventoryItems.data.forEach(item => {
      const key = `${item.fk_product_id}-${item.fk_rate_id}`;

      if (!groups[key]) {
        groups[key] = {
          productId: item.fk_product_id,
          rateId: item.fk_rate_id,
          productName: item.Product.product_name ?? '',
          totalQuantity: 0,
          wholesalePrice: item.Rates.wholesale_price,
          factoredPrice: item.Rates.factored_price,
          totalCost: 0,
          wholesaleQuantity: 0,
          factoredQuantity: 0,
          items: [],
          rate: item.Rates,
        };
      }

      groups[key].totalQuantity++;
      groups[key].items.push(item);
    });

    return Object.values(groups).sort((a, b) => a.wholesalePrice - b.wholesalePrice);
  }, [inventoryItems]);

  const handleViewRate = (rate: Database['public']['Tables']['Rates']['Row']) => {
    setSelectedRate(rate);
    setShowRateModal(true);
  };

  /**********************************************
   * FUNCTIONS
   **********************************************/
  const updateQuantity = (index: number, type: 'wholesale' | 'factored', increment: boolean) => {
    const newItems = [...groupedItems];
    const item = newItems[index];

    if (type === 'wholesale') {
      if (increment) {
        if (item.wholesaleQuantity + item.factoredQuantity < item.totalQuantity) {
          item.wholesaleQuantity++;
        }
      } else if (item.wholesaleQuantity > 0) {
        item.wholesaleQuantity--;
      }
    } else {
      if (increment) {
        if (item.wholesaleQuantity + item.factoredQuantity < item.totalQuantity) {
          item.factoredQuantity++;
        }
      } else if (item.factoredQuantity > 0) {
        item.factoredQuantity--;
      }
    }

    item.totalCost = item.wholesaleQuantity * item.wholesalePrice + item.factoredQuantity * item.factoredPrice;
    setSelectedItems(newItems);
  };

  /**
   * Submit the Invoice
   */
  const handleSubmitInvoice = async (data: InvoiceT) => {
    try {
      // Validate form
      const isValid = await trigger();
      if (!isValid) {
        toast.error('Please fill in all required fields correctly');
        return;
      }

      const invoiceData = {
        invoice_date: data.invoice_date_issued,
        invoice_due_date: data.invoice_due_date,
        invoice_description: data.invoice_description,
        invoice_contact_name: data.invoice_contact_name,
        invoice_contact_email: data.invoice_contact_email,
        invoice_contact_phone: data.invoice_contact_phone,
        invoice_contact_address: data.invoice_contact_address,
        fk_retailer_id: type === 'retailer' ? retailer?.retailer_id : null,
        fk_distributor_id: type === 'distributor' ? distributor?.distributor_id : null,
      };

      const invoiceItems = selectedItems.flatMap(item => {
        const items = [];
        if (item.wholesaleQuantity > 0) {
          items.push({
            fk_product_id: item.productId,
            fk_rate_id: item.rateId,
            invoice_item_quantity: item.wholesaleQuantity,
            invoice_item_unit_price: item.wholesalePrice,
            invoice_item_total_cost: item.wholesaleQuantity * item.wholesalePrice,
            invoice_item_price_type: 'wholesale',
          });
        }
        if (item.factoredQuantity > 0) {
          items.push({
            fk_product_id: item.productId,
            fk_rate_id: item.rateId,
            invoice_item_quantity: item.factoredQuantity,
            invoice_item_unit_price: item.factoredPrice,
            invoice_item_total_cost: item.factoredQuantity * item.factoredPrice,
            invoice_item_price_type: 'factored',
          });
        }
        return items;
      });

      const {data: user} = await SupabaseInstance.supabase.auth.getUser();

      if (!user?.user) {
        throw new Error('User not found');
      }

      const {error} = await SupabaseInstance.supabase.functions.invoke('create-invoice', {
        body: {
          user_uid: user?.user.id,
          invoice_data: invoiceData,
          invoice_items: invoiceItems,
        },
      });

      if (error) {
        throw new Error('Failed to create invoice');
      }

      toast.success('Invoice created successfully');
      reset(); // Reset form
      refetchInventoryItems(); // Refetch inventory items
      navigate('/admin/invoices'); // Navigate back to invoices page
    } catch (error) {
      console.error('Error creating invoice:', error);
      refetchInventoryItems();
      toast.error('Failed to create invoice. Please try again.');
    }
  };

  return (
    <FormProvider {...formConfig}>
      <form onSubmit={handleSubmit(handleSubmitInvoice)}>
        <div className="mt-2 grid grid-cols-12 gap-6">
          <div className="col-span-12 xl:col-span-12">
            <div className="box">
              <div className="box-header">
                <h6 className="box-title">
                  Create Invoice for {type === 'distributor' ? distributor?.distributor_name : retailer?.retailer_name}
                </h6>
              </div>
              <div className="box-body">
                <div className="grid grid-cols-12 gap-y-6 sm:gap-x-6">
                  <div className="col-span-12 xl:col-span-12">
                    <div className="grid grid-cols-12 gap-y-6 sm:gap-x-6">
                      <div className="col-span-12 sm:col-span-6 md:col-span-6 lg:col-span-6 xl:col-span-4">
                        <p className="mb-2 font-semibold">Billing From :</p>
                        <div className="col-span-12 grid grid-cols-12">
                          <div className="col-span-12 xl:col-span-12">
                            <p className="form-control w-full !rounded-md text-xl">TreesNClouds</p>
                          </div>
                          <div className="col-span-12 xl:col-span-12">
                            <p className="form-control w-full !rounded-md text-xl">123 main street</p>
                          </div>
                          <div className="col-span-12 xl:col-span-12">
                            <p className="form-control w-full !rounded-md text-xl">info@treesnclouds.com</p>
                          </div>
                          <div className="col-span-12 xl:col-span-12">
                            <p className="form-control w-full !rounded-md text-xl">+1234567890</p>
                          </div>
                        </div>
                      </div>
                      <div className="hidden xl:col-span-4 xl:flex"></div>
                      <div className="col-span-12 sm:col-span-6 md:col-span-6 lg:col-span-6 xl:col-span-4">
                        <p className="mb-2 font-semibold">Billing To :</p>
                        <div className="grid grid-cols-12 gap-2">
                          {formFields.slice(0, 4).map((field, index) => (
                            <>
                              <div className="col-span-12">
                                <FormInput key={index} field={field} hideLabel />
                              </div>
                            </>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-span-12 mt-5 flex flex-row justify-end gap-2 border-t border-defaultborder/10 pt-5">
                    <div className="col-span-12 xl:col-span-3">
                      <label htmlFor="invoice-date-issued" className="form-label">
                        Date Issued
                      </label>

                      <FormInput hideLabel field={formFields[4]} />
                    </div>
                    <div className="col-span-12 xl:col-span-3">
                      <label className="form-label">Due Date</label>

                      <FormInput hideLabel field={formFields[5]} />
                    </div>
                  </div>
                  {/* Invoice Items Table */}
                  <div className="col-span-12 xl:col-span-12">
                    <div className="table-responsive w-full">
                      <table className="mt-3 table w-full whitespace-nowrap border dark:border-defaultborder/10">
                        <thead>
                          <tr>
                            <th scope="row">PRODUCT NAME</th>
                            <th scope="row">WHOLESALE</th>
                            <th scope="row">FACTORED</th>
                            <th scope="row">WHOLESALE PRICE</th>
                            <th scope="row">FACTORED PRICE</th>
                            <th scope="row">TOTAL COST</th>
                            <th scope="row">ACTIONS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedItems.length > 0 &&
                            groupedItems.map((item, index) => (
                              <tr key={index} className="border border-defaultborder dark:border-defaultborder/10">
                                <td>
                                  <span className="w-full !rounded-md">
                                    {item.productName} (Available: {item.totalQuantity})
                                  </span>
                                </td>
                                <td className="invoice-quantity-container">
                                  <div className="input-group !flex-nowrap rounded-md border dark:border-defaultborder/10">
                                    <button
                                      aria-label="button"
                                      type="button"
                                      className="ti-btn ti-btn-icon input-group-text product-quantity-minus !mb-0 !rounded-e-none !bg-primary !text-white"
                                      onClick={() => updateQuantity(index, 'wholesale', false)}
                                    >
                                      <i className="ri-subtract-line"></i>
                                    </button>
                                    <input
                                      type="text"
                                      className="form-control !w-12 border-0 !p-1 text-center"
                                      value={item.wholesaleQuantity}
                                      readOnly
                                    />
                                    <button
                                      aria-label="button"
                                      type="button"
                                      className="ti-btn ti-btn-icon input-group-text product-quantity-plus !mb-0 !rounded-s-none !bg-primary !text-white"
                                      onClick={() => updateQuantity(index, 'wholesale', true)}
                                    >
                                      <i className="ri-add-line"></i>
                                    </button>
                                  </div>
                                </td>
                                <td className="invoice-quantity-container">
                                  <div className="input-group !flex-nowrap rounded-md border dark:border-defaultborder/10">
                                    <button
                                      aria-label="button"
                                      type="button"
                                      className="ti-btn ti-btn-icon input-group-text product-quantity-minus !mb-0 !rounded-e-none !bg-primary !text-white"
                                      onClick={() => updateQuantity(index, 'factored', false)}
                                    >
                                      <i className="ri-subtract-line"></i>
                                    </button>
                                    <input
                                      type="text"
                                      className="form-control !w-12 border-0 !p-1 text-center"
                                      value={item.factoredQuantity}
                                      readOnly
                                    />
                                    <button
                                      aria-label="button"
                                      type="button"
                                      className="ti-btn ti-btn-icon input-group-text product-quantity-plus !mb-0 !rounded-s-none !bg-primary !text-white"
                                      onClick={() => updateQuantity(index, 'factored', true)}
                                    >
                                      <i className="ri-add-line"></i>
                                    </button>
                                  </div>
                                </td>
                                <td>
                                  <span className="w-full !rounded-md">{usdFormatter.format(item.wholesalePrice)}</span>
                                </td>
                                <td>
                                  <span className="w-full !rounded-md">{usdFormatter.format(item.factoredPrice)}</span>
                                </td>
                                <td>
                                  <span className="w-full !rounded-md">{usdFormatter.format(item.totalCost)}</span>
                                </td>
                                <td>
                                  <button
                                    className="ti-btn ti-btn-primary !mb-0"
                                    onClick={() => {
                                      if (!item.rate) toast.error('No rate found for this item');
                                      else handleViewRate(item.rate);
                                    }}
                                  >
                                    View Rate
                                  </button>
                                </td>
                              </tr>
                            ))}

                          <tr>
                            <td colSpan={5}></td>
                            <td colSpan={2}>
                              <table className="table-sm table-borderless mb-0 table w-full whitespace-nowrap dark:border-defaultborder/10">
                                <tbody>
                                  <tr>
                                    <th scope="row">
                                      <div className="font-semibold">Sub Total :</div>
                                    </th>
                                    <td>
                                      <input
                                        type="text"
                                        className="form-control invoice-amount-input w-full !rounded-md !bg-light"
                                        value={usdFormatter.format(
                                          groupedItems.reduce((sum, item) => sum + item.totalCost, 0)
                                        )}
                                        readOnly
                                      />
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="col-span-12 xl:col-span-12">
                    <div>
                      <label htmlFor="invoice-note" className="form-label">
                        Invoice Description:
                      </label>
                      {/* <textarea
                          value={invoiceDescription}
                          className="form-control !bg-light w-full !rounded-md"
                          id="invoice-note"
                          rows={3}
                          placeholder="Enter Invoice Description here. This will be displayed on the invoice."
                          onChange={e => setInvoiceDescription(e.target.value)}
                        ></textarea> */}
                      <FormInput field={formFields[6]} hideLabel />
                    </div>
                  </div>
                </div>
              </div>
              <div className="box-footer text-end">
                <button type="submit" className="ti-btn !mb-0 bg-primary text-white">
                  Create Invoice
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Rate Details Modal */}
        {showRateModal && selectedRate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-96 rounded-lg bg-black p-6 shadow-lg">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold">Rate Details</h3>
                <button onClick={() => setShowRateModal(false)} className="text-gray-500 hover:text-gray-700">
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-100">Wholesale Price</p>
                  <p className="font-semibold text-gray-400">{usdFormatter.format(selectedRate.wholesale_price)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-100">TNC Price</p>
                  <p className="font-semibold text-gray-400">{usdFormatter.format(selectedRate.tnc_price)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-100">Factored Price</p>
                  <p className="font-semibold text-gray-400">{usdFormatter.format(selectedRate.factored_price)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-100">Created At</p>
                  <p className="font-semibold text-gray-400">
                    {new Date(selectedRate.date_created).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-100">Updated At</p>
                  <p className="font-semibold text-gray-400">
                    {new Date(selectedRate.date_modified).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="mt-6 text-right">
                <button onClick={() => setShowRateModal(false)} className="ti-btn bg-primary text-white">
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </form>
    </FormProvider>
  );
}

export default CreateInvoicePage;
